export type FaqGroup = 'Company' | 'Products & science' | 'Approvals & regulation' | 'Buying & partnering'

export interface FaqItem {
  q: string
  a: string
  group: FaqGroup
}

/**
 * Site-wide FAQ. Every answer is grounded in the product sheets, the company record or how the
 * website itself actually works — nothing here describes a capability the business does not have.
 *
 * Grouped because the questions come from different people. A farmer asks how to choose a product; a
 * retailer asks how to stock it; a compliance reader asks what an approval covers. Product-specific
 * questions are NOT duplicated here: each product page generates its own FAQ from its own record
 * (lib/productFaq), so a composition or dose appears once, beside the product it belongs to.
 */
export const faqs: FaqItem[] = [
  // ── Company ───────────────────────────────────────────────────────────────
  {
    group: 'Company',
    q: 'What is GreenbayBio?',
    a: 'GreenbayBio is the agricultural bio-input brand of Greenbay Biotech International Pvt Ltd., an Indian company based in Kolkata, West Bengal. It supplies bio-fertilizers, soil improvers, biostimulants, plant-strength foliar products and root-zone water management for Indian agriculture, through a retailer and distributor network.',
  },
  {
    group: 'Company',
    q: 'Is GreenbayBio the same as Greenbay Biotech International?',
    a: 'Yes. Greenbay Biotech International Pvt Ltd. is the registered company; GreenbayBio is the brand its products are sold under. Both names refer to the same business.',
  },
  {
    group: 'Company',
    q: 'What kinds of products does GreenbayBio make?',
    a: 'Bio-based crop inputs across five categories: soil-biology bio-fertilizers (AGN, AGN LTE, BIODYNAMIC), soil improvers (Biovin), biostimulants (Fulvic 25, AminOPF), plant-strength foliars (C4L Biofoliar, Nature’s Green 4+, Plant Armor Elite) and water management (Hydretain).',
  },
  {
    group: 'Company',
    q: 'Does GreenbayBio manufacture everything it sells?',
    a: 'Not necessarily, and the site does not claim it does. The range brings together products developed by different technology partners — soil-biology, humic, botanical and moisture-management specialists — adapted for Indian crops and conditions. Where a product’s own technical sheet states who manufactures or markets it, that statement appears on the product page.',
  },

  // ── Products & science ────────────────────────────────────────────────────
  {
    group: 'Products & science',
    q: 'How do I choose the right product for my crop?',
    a: 'Start from the Crop Solutions section. Each crop page lists the products documented for that crop and reproduces the application stage and dose from each product’s own technical specifications. If your crop is not listed, or field conditions differ, contact us and our team will help build a programme.',
  },
  {
    group: 'Products & science',
    q: 'What is the difference between a bio-fertilizer and a biostimulant?',
    a: 'A bio-fertilizer contains living microorganisms and works by making nutrients available — fixing nitrogen, or solubilising phosphorus already present in the soil. A biostimulant works on the plant’s own physiology: nutrient-use efficiency, tolerance to stress, or crop quality, independently of its own nutrient content. GreenbayBio’s range includes both, in separate categories.',
  },
  {
    group: 'Products & science',
    q: 'How do bio-fertilizers like AGN and BIODYNAMIC work?',
    a: 'They introduce phosphate-solubilizing bacteria (PSB) into the rhizosphere — the soil immediately around plant roots. Those bacteria release organic acids and enzymes that free phosphorus held in forms plants cannot take up, and they occupy root-surface space and resources that harmful organisms would otherwise use. The microbial count each product is specified against is printed on its page.',
  },
  {
    group: 'Products & science',
    q: 'Why do some products need to be incubated before use?',
    a: 'The AGN family holds its bacteria as dormant, encapsulated spores so the microbial count stays stable in storage. Mixing with water at the ratio on the label and standing for at least one hour activates them before the product goes into the soil. Applying without that step applies dormant cells.',
  },
  {
    group: 'Products & science',
    q: 'Can these products be mixed with pesticides or fungicides?',
    a: 'For the microbial bio-fertilizers — AGN, AGN LTE and BIODYNAMIC — no. The active ingredient is alive and these products will kill it; the labels specify a gap of at least 48 hours before or after. Several biostimulants and foliar products state that they can be applied with other inputs. Always check the How to use section on the specific product page, and the label on your pack.',
  },
  {
    group: 'Products & science',
    q: 'How should products be stored, and how long do they last?',
    a: 'Store products dry, frost-free and away from direct sunlight. Shelf life differs by product and is listed per product in the register on the Resources page and on each product page — for a living product it is the period over which the stated microbial count is expected to hold.',
  },
  {
    group: 'Products & science',
    q: 'Do you guarantee higher yields or income?',
    a: 'No. We do not promise guaranteed yields or income, and we do not publish a percentage improvement figure. Our products are designed to support soil biology, nutrient availability and plant strength; what that produces in a given field depends on soil condition, climate, crop and management, none of which we control.',
  },
  {
    group: 'Products & science',
    q: 'Are the products safe to handle?',
    a: 'They are crop inputs, not for ingestion. Wash hands after handling, wear protective clothing where the label indicates it, keep products away from children, food and feed, and follow the precautions printed on the label and listed on each product page.',
  },
  {
    group: 'Products & science',
    q: 'Are the technical data sheets available in Hindi?',
    a: 'Yes — most products have both an English and a Hindi data sheet, and both are linked from the product page and from the register on the Resources page. The website pages themselves are currently in English only.',
  },

  // ── Approvals & regulation ────────────────────────────────────────────────
  {
    group: 'Approvals & regulation',
    q: 'Are GreenbayBio products organic?',
    a: 'Approvals differ product by product, so there is no single answer for the range. Several products are approved for use in organic farming under NPOP (India); some are also registered under India’s Fertiliser (Control) Order; Biovin holds an EU organic biostimulant approval; and Hydretain is approved under the USDA BioPreferred programme for verified bio-based content. The exact wording for each product is on its own page and in the register on the Resources page.',
  },
  {
    group: 'Approvals & regulation',
    q: 'Is FCO registration the same as NPOP approval?',
    a: 'No. Registration under India’s Fertiliser (Control) Order is the regulatory permission to sell a product as a fertiliser in India, and it carries the product’s specification. NPOP approval says an input may be used in a certified organic system. They are separate instruments, and a product may hold one, both or neither.',
  },
  {
    group: 'Approvals & regulation',
    q: 'Does an approval from the EU or the USA count as Indian approval?',
    a: 'No. An approval granted under an overseas standard — an EU regulation, or a United States programme — is a statement made by that standard, not Indian regulatory approval. Where a product holds an overseas approval, its page names the standard so it is clear which one applies.',
  },

  // ── Buying & partnering ───────────────────────────────────────────────────
  {
    group: 'Buying & partnering',
    q: 'Where can I buy GreenbayBio products?',
    a: 'Through GreenbayBio retailers and distributors. Send a product enquiry from any product page and the website will help identify the nearest dealer for your location and pass your enquiry to the team covering your area; you can then track its status from the link you are given.',
  },
  {
    group: 'Buying & partnering',
    q: 'Can farmers order directly from this website?',
    a: 'No. The website is for product information and enquiries — there is no online checkout for farmers, and prices are not published here. Products reach farmers through the retailer and distributor network, and an enquiry raised on the site is routed to the nearest dealer and the sales team for your area.',
  },
  {
    group: 'Buying & partnering',
    q: 'How can I become a GreenbayBio retailer?',
    a: 'Use the Become a retailer enquiry page, or contact us directly. Tell us your location and the crops your customers grow, and our team will get back to you about stocking the range.',
  },
  {
    group: 'Buying & partnering',
    q: 'How can I become a GreenbayBio distributor?',
    a: 'Use the Become a distributor enquiry page, or contact us directly. Our team will discuss the territory, the retailer network and how a partnership would work.',
  },
  {
    group: 'Buying & partnering',
    q: 'Can I get agronomic advice for my field?',
    a: 'Yes — contact us and describe your crop, stage and conditions. Programmes on this site are written from the product specifications and are a starting point; where soil, climate or field condition differs, a crop advisor should set the dose and timing rather than the website.',
  },
]

export const FAQ_GROUPS: FaqGroup[] = ['Company', 'Products & science', 'Approvals & regulation', 'Buying & partnering']

/** FAQ entries for one group, in authored order. */
export function faqsInGroup(group: FaqGroup): FaqItem[] {
  return faqs.filter((f) => f.group === group)
}
